<script setup lang="ts">
import { computed, ref } from 'vue'
import { onClickOutside } from '@vueuse/core'
import {
  VButton,
  VCard,
  VCardTitle,
  VDescriptionList,
  VDescriptionListDetailed,
  VIcon,
  VOperatingActivity,
  VMetricCard,
  VStats,
  ActionsGroup,
  ActionsMenu,
  ActionItem,
} from '@/modules/shared/components'
import type { Stat, DescriptionListDetailedItem, OperatingActivityItem } from '@/modules/shared/components/types'
import LifecycleDateBanner from '@/modules/investing/components/lifecycle-date-banner.vue'
import SpvOverviewHeader from '@/modules/investing/components/spv-overview-header.vue'
import OnboardingProgressBar from '@/modules/connecting/components/onboarding-progress-bar.vue'
import {
  ArrowDownToLine,
  ArrowUpRight,
  Banknote,
  BarChart3,
  Briefcase,
  Building2,
  Check,
  ChevronDown,
  Diamond,
  ChevronLeft,
  ChevronRight,
  ChevronUp,
  Equal,
  Home,
  Landmark,
  Layers,
  Network,
  Percent,
  ReceiptText,
  Repeat,
  RotateCcw,
  Search,
  Settings,
  Signature,
  Star,
  TrendingUp,
  Trophy,
  Users,
  Wallet,
} from 'lucide-vue-next'

type LifecycleState = 'raising' | 'closed' | 'exited'

const currentState = ref<LifecycleState>('closed')
const sidebarLocked = ref(true)
const showSpvNav = ref(false)
const stageMenuOpen = ref(false)
const stageMenuRef = ref()
const headerVariant = ref<number>(0)
const showSublabels = ref(true)

onClickOutside(stageMenuRef, () => (stageMenuOpen.value = false))

const stageLabel = computed(() => {
  switch (currentState.value) {
    case 'raising':
      return 'Raising'
    case 'closed':
      return 'Closed'
    case 'exited':
      return 'Exited'
    default:
      return '—'
  }
})

const lifecycleStepIndex = computed(() => {
  switch (currentState.value) {
    case 'raising':
      return 0
    case 'closed':
      return 1
    case 'exited':
      return 2
    default:
      return 0
  }
})

function selectStage(key: LifecycleState) {
  currentState.value = key
  stageMenuOpen.value = false
}

const states: { key: LifecycleState; label: string }[] = [
  { key: 'raising', label: 'Raising' },
  { key: 'closed', label: 'Closed' },
  { key: 'exited', label: 'Exited' },
]

const closedData = {
  totalCommitted: '$500,000',
  investorCount: 22,
  closedDate: 'Jun 3, 2026',
  paidCalled: '$475,000',
  contributed: '$450,000',
  allFees: '$12,500',
  fundsDeployed: '$437,500',
  cashPosition: '$25,000',
  roi: 12.4,
  moic: 1.12,
  xirr: 8.7,
  realizedGain: { amount: '0.00', currency: 'USD', date: '' } as any,
  unrealizedGain: { amount: '62500.00', currency: 'USD', date: '' } as any,
  tvpi: 1.12,
  rvpi: 1.12,
  dpi: 0.08,
}

const mock = {
  entity: {
    name: 'Stripe Series I Follow-On SPV',
    legalEntityName: 'Stripe Series I Follow-On SPV, LLC (Delaware)',
    companyName: 'Stripe',
    companyWebsite: 'https://stripe.com',
    companyDescription:
      "Stripe is a global technology company that builds economic infrastructure for the internet. Businesses of every size use Stripe's software and APIs to accept payments, send payouts, and manage their businesses online.",
    sectors: ['Fintech', 'Payments'],
    stage: 'Series I',
    equityType: 'Preferred Equity',
    preMoneyValuation: '$70,000,000,000',
    targetRaise: '$500,000',
    minimumInvestment: '$25,000',
    managementFee: '2.0%',
    carriedInterest: '20.0%',
    preferredReturn: '8.0%',
    formedDate: 'Jan 15, 2026',
    managerName: 'Michael Johnson',
    ein: '87-1234567',
  },
  closed: closedData,
  exited: {
    ...closedData,
    exitedDate: 'Mar 15, 2027',
    exitType: 'Acquisition',
    grossProceeds: '$750,000',
    carriedInterest: '$50,000',
    distributedFees: '$5,000',
    netDistribution: '$695,000',
  },
}

const raisingData = {
  totalCommitted: '$75,000',
  targetRaise: '$500,000',
  investorCount: 2,
  remaining: '$425,000',
  raiseProgress: 0.15,
  daysRemaining: 82,
  closingDate: 'Aug 15, 2026',
  capitalPaid: '$25,000',
  capitalCalled: '$75,000',
  capitalProgress: 0.333,
  docsSigned: 1,
  docsTotal: 2,
  docsProgress: 0.5,
  investmentCompleted: 0,
  roi: 0,
  moic: null,
  xirr: null,
  realizedGain: null,
  unrealizedGain: null,
  tvpi: null,
  rvpi: null,
  dpi: null,
}

const data = computed(() => {
  if (currentState.value === 'raising') return raisingData
  return mock[currentState.value as 'closed' | 'exited']
})

const tabs = [
  'Overview',
  'Portfolio',
  'Investors',
  'Transactions',
  'Transfers',
  'Capital Account',
  'eSignatures',
  'Files',
  'Notes',
]

const navItems = [
  { key: 'home', label: 'Dashboard', icon: Home },
  { key: 'portfolio', label: 'Portfolio', icon: BarChart3 },
  { key: 'companies', label: 'Companies', icon: Landmark },
  { key: null, label: null, icon: null }, // divider
  { key: 'funds', label: 'Funds', icon: Wallet },
  { key: 'spvs', label: 'SPVs', icon: Layers },
  { key: 'entities', label: 'Associated Entities', icon: Building2 },
  { key: 'individuals', label: 'Individuals', icon: Users },
  { key: null, label: null, icon: null }, // divider
  { key: 'taxes', label: 'Taxes', icon: ReceiptText },
  { key: 'connections', label: 'Connections', icon: Network },
  { key: 'settings', label: 'Settings', icon: Settings },
] as const

const spvList = [
  { name: 'Anthropic Series D SPV', lifecycle: 'closed' },
  { name: 'SpaceX Series J SPV', lifecycle: 'raising' },
  { name: 'Notion Series C SPV', lifecycle: 'exited' },
  { name: 'Stripe Series I Follow-On SPV', lifecycle: 'closed' },
  { name: 'Figma Series E SPV', lifecycle: 'exited' },
  { name: 'Plaid Series D SPV', lifecycle: 'raising' },
]

const lifecycleSteps = computed(() => [
  {
    label: 'Raising',
    state: 'raising' as const,
    active: currentState.value === 'raising',
    completed: currentState.value !== 'raising',
    future: false,
    date: mock.entity.formedDate,
  },
  {
    label: 'Closed',
    state: 'closed' as const,
    active: currentState.value === 'closed',
    completed: currentState.value === 'exited',
    future: currentState.value === 'raising',
    date: currentState.value === 'raising' ? null : mock.closed.closedDate,
  },
  {
    label: 'Exited',
    state: 'exited' as const,
    active: false,
    completed: currentState.value === 'exited',
    future: currentState.value !== 'exited',
    date: currentState.value === 'exited' ? mock.exited.exitedDate : null,
  },
])

const kpiStats = computed<Stat[]>(() => {
  const d = data.value
  return [
    {
      label: 'ROI',
      sublabel: showSublabels.value ? 'return on investment' : undefined,
      value: d.roi != null && d.roi !== 0 ? d.roi / 100 : '—',
      type: d.roi != null && d.roi !== 0 ? ('percent' as const) : undefined,
      colspan: 1,
    },
    {
      label: 'XIRR',
      sublabel: showSublabels.value ? 'extended IRR' : undefined,
      value: d.xirr != null && d.xirr !== 0 ? d.xirr / 100 : '—',
      type: d.xirr != null && d.xirr !== 0 ? ('percent' as const) : undefined,
      colspan: 1,
    },
    {
      label: 'Realized G/L',
      sublabel: showSublabels.value ? 'distributions received' : undefined,
      value: d.realizedGain ?? '—',
      type: d.realizedGain ? ('currency' as const) : undefined,
      colspan: 1,
    },
    {
      label: 'Unrealized G/L',
      sublabel: showSublabels.value ? 'unrealized value change' : undefined,
      value: d.unrealizedGain ?? '—',
      type: d.unrealizedGain ? ('currency' as const) : undefined,
      colspan: 1,
    },
  ]
})

const committedPercent = computed(() => {
  const committed = parseFloat(raisingData.totalCommitted.replace(/[^0-9.]/g, ''))
  const target = parseFloat(mock.entity.targetRaise.replace(/[^0-9.]/g, ''))
  if (target === 0) return 0
  return Math.round((committed / target) * 100)
})

const raiseCommitted = computed(() => ({
  value: raisingData.totalCommitted,
  width: Math.min(committedPercent.value, 100),
  color: '#2E8B91',
  label: 'Committed',
}))

const raiseActivityRows = computed<DescriptionListDetailedItem[]>(() => [
  {
    icon: Signature,
    label: 'Documents Signed',
    sublabel: 'agreements executed by investors',
    value: `${raisingData.docsSigned} / ${raisingData.docsTotal}`,
    color: '#64748B',
    actionLabel: 'Request Signatures',
  },
  {
    icon: ArrowDownToLine,
    label: 'Capital Paid',
    sublabel: 'received from investors',
    value: raisingData.capitalPaid,
    color: '#3092b3',
    actionLabel: 'Call Capital',
  },
  {
    icon: Briefcase,
    label: 'Investment Completed',
    sublabel: 'capital deployed into target company',
    value: 'No',
    color: '#059669',
    actionLabel: 'Complete Investment',
  },
])

function handleRaiseAction(item: DescriptionListDetailedItem) {
  console.log('raise action:', item.label)
}

const raiseRemaining = computed(() => {
  if (committedPercent.value >= 100) return undefined
  return {
    value: raisingData.remaining,
    width: 100 - committedPercent.value,
    color: '#D2D5DB',
    label: 'Remaining',
    style: 'solid' as const,
  }
})

const dealItems = computed(() => {
  const items = [
    { term: 'Sector', description: mock.entity.sectors.join(', ') },
    { term: 'Stage', description: mock.entity.stage },
    { term: 'Equity Type', description: mock.entity.equityType },
    { term: 'Pre-Money Valuation', description: mock.entity.preMoneyValuation },
  ]
  items.push(
    { term: 'Total Raise', description: mock.entity.targetRaise },
    { term: 'Minimum Investment', description: mock.entity.minimumInvestment },
  )
  return items
})

const feeItems = computed(() => [
  { term: 'Fees & Economics', description: '' },
  { term: 'Management Fee', description: mock.entity.managementFee },
  { term: 'Carried Interest', description: mock.entity.carriedInterest },
  { term: 'Preferred Return', description: mock.entity.preferredReturn },
])

const capitalSummaryRows = computed<DescriptionListDetailedItem[]>(() => [
  {
    icon: ArrowDownToLine,
    label: 'Capital Paid',
    sublabel: 'received from investors',
    value: '$350,000',
    color: '#3092b3',
  },
  {
    icon: Percent,
    label: 'Total Fees',
    sublabel: 'management + other fees',
    value: '$12,500',
    color: '#7d8ad0',
  },
  {
    icon: TrendingUp,
    label: 'Funds Deployed',
    sublabel: 'net capital deployed into investments',
    value: '$262,500',
    color: '#246586',
  },
  {
    icon: Banknote,
    label: 'Cash Position',
    sublabel: 'available cash balance',
    value: '$17,500',
    color: '#059669',
  },
])

const distributionSummaryRows = computed<DescriptionListDetailedItem[]>(() => [
  {
    icon: RotateCcw,
    label: 'Return of Capital',
    sublabel: 'capital returned to investors',
    value: '$175,000',
    color: '#408F93',
  },
  {
    icon: Star,
    label: 'Preferred Return',
    sublabel: 'priority return to investors',
    value: '$50,000',
    color: '#4FB8A9',
  },
  {
    icon: ArrowUpRight,
    label: 'Profit',
    sublabel: 'gains above committed capital',
    value: '$0',
    color: '#46B37D',
  },
  {
    icon: Diamond,
    label: 'Carried Interest',
    sublabel: 'GP profit share',
    value: '$0',
    color: '#7d8ad0',
  },
  {
    icon: Equal,
    label: 'Net Distributed',
    sublabel: 'distributed to investors',
    value: '$250,000',
    color: '#246586',
  },
])

const operatingActivityItems = computed<OperatingActivityItem[]>(() => [
  { label: 'Operating Income', value: '$12,500', color: '#a78bfa' },
  { label: 'Operating Expenses', value: '$8,200', color: '#c4b5fd' },
  { label: 'Interest Paid', value: '$3,100', color: '#8b5cf6' },
  { label: 'Interest Allocated', value: '$1,400', color: '#ddd6fe' },
])

const calledPaid = { value: '$350,000', width: 70, color: '#3092b3', gradientTo: '#246586' }
const calledPending = { value: '$100,000', width: 90, color: '#91c4d9' }
const calledTotal = { value: '$500,000', label: 'Committed' }

const distPaid = { value: '$250,000', width: 50, color: '#408F93', gradientTo: '#246586' }
const distPending = { value: '$50,000', width: 100, color: '#7dc9cc' }

const distOperatingActivityItems = computed<OperatingActivityItem[]>(() => [
  { label: 'Other Fees', value: '$6,200', color: '#7d8ad0' },
  { label: 'Recycled Funds', value: '$18,000', color: '#246586' },
])

const fileItems = [
  { name: 'SPV Operating Agreement.pdf', size: '245 KB', type: 'application/pdf' },
  { name: 'Subscription Agreement.pdf', size: '180 KB', type: 'application/pdf' },
  { name: 'Private Placement Memo.pdf', size: '420 KB', type: 'application/pdf' },
]

const fileIconMap: Record<string, string> = {
  'application/pdf': 'PdfTypeDefault',
  'application/msword': 'DocTypeDefault',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': 'DocxTypeDefault',
  'application/vnd.ms-excel': 'XlsTypeDefault',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': 'XlsxTypeDefault',
  'image/png': 'ImageTypeDefault',
  'image/jpeg': 'ImageTypeDefault',
  'application/zip': 'ZipTypeDefault',
}

function fileIconForType(type: string): string {
  return fileIconMap[type] ?? 'EmptyTypeDefault'
}

function openFile() {
  window.open('#', '_blank')
}

function downloadFile() {
  console.log('download file (mock)')
}
</script>

<template>
  <!-- ======================================================================== -->
  <!-- Dev Layout Shell                                                          -->
  <!-- ======================================================================== -->
  <div class="fixed left-0 right-0 top-0 z-50 h-5">
    <OnboardingProgressBar :current="0" :total="1" />
  </div>

  <div class="fixed inset-0 flex overflow-hidden bg-white">
    <!-- Side Nav -->
    <aside
      class="relative z-20 hidden h-full flex-none break-all border-r border-gray-200 bg-white sm:block"
      :class="sidebarLocked ? 'w-[220px]' : 'w-16'"
    >
      <div class="absolute left-5 top-[24px] z-10">
        <img src="/v-logo-192x192.png" class="w-6" />
      </div>
      <nav class="z-10 mt-16 px-3 pt-5">
        <ul>
          <li v-for="item in navItems" :key="item.key ?? item.label">
            <div v-if="!item.key" class="my-2 flex items-center justify-center">
              <div v-if="sidebarLocked" class="w-full border-t border-gray-200" />
              <div v-else class="h-1 w-1 rounded-full bg-gray-300" />
            </div>
            <a
              v-else
              class="group relative flex cursor-pointer items-center gap-4 whitespace-nowrap rounded-md px-1.5 py-0.5 text-sm font-medium leading-4"
              :class="[
                item.key === 'spvs'
                  ? 'text-[#3b88af] hover:bg-[#3b88af]/10 active:bg-[#3b88af]/20'
                  : 'text-gray-700 hover:bg-gray-900/5 active:bg-gray-200',
              ]"
            >
              <span class="inline-flex flex-shrink-0 items-center justify-center rounded-md bg-transparent p-1.5">
                <component :is="item.icon" class="h-4 w-4" />
              </span>
              <span
                class="overflow-hidden whitespace-nowrap"
                :class="sidebarLocked ? 'w-auto opacity-100' : 'w-0 opacity-0'"
                >{{ item.label }}</span
              >
              <span
                v-if="!sidebarLocked"
                class="pointer-events-none absolute left-full ml-3 hidden whitespace-nowrap rounded-md bg-gray-900 px-2.5 py-1.5 text-xs font-medium text-white shadow-sm group-hover:block"
              >
                {{ item.label }}
              </span>
            </a>
          </li>
        </ul>
      </nav>
      <div class="absolute bottom-3 left-0 right-0 flex justify-center">
        <VButton size="xs-narrow" variant="invisible" @click="sidebarLocked = !sidebarLocked">
          <component :is="sidebarLocked ? ChevronLeft : ChevronRight" :size="16" />
        </VButton>
      </div>
    </aside>

    <!-- Secondary SPV Nav -->
    <aside v-if="showSpvNav" class="w-72 flex-none overflow-y-auto border-r border-gray-200 bg-white">
      <div class="px-3 pt-[25px]">
        <div class="relative">
          <input
            type="text"
            placeholder="Search…"
            class="relative block w-full whitespace-nowrap rounded-md border-none bg-white py-1 pl-2 pr-7 text-sm font-semibold text-gray-800 shadow-sm ring-1 ring-gray-700/20 hover:shadow-md hover:ring-gray-700/30 hover:transition-shadow focus:outline-none active:shadow-sm active:transition-none"
          />
          <Search :size="14" class="pointer-events-none absolute right-1.5 top-1/2 -translate-y-1/2 text-gray-400" />
        </div>
      </div>
      <div class="px-3 pt-[39px]">
        <h3 class="text-xs font-semibold uppercase tracking-wider text-gray-500">SPVs</h3>
      </div>
      <nav class="mt-4">
        <div
          v-for="spv in spvList"
          :key="spv.name"
          class="mx-3 cursor-pointer truncate rounded-md px-3 py-2 text-sm"
          :class="spv.name === mock.entity.name ? 'bg-[#3b88af]/10 text-[#3b88af]' : 'text-gray-700 hover:bg-gray-100'"
        >
          {{ spv.name }}
        </div>
      </nav>
    </aside>

    <!-- Main content area -->
    <main class="h-full flex-grow overflow-y-auto bg-gray-50">
      <div class="w-[95%] pb-8 sm:mx-8 sm:w-auto sm:px-0" :class="headerVariant >= 3 ? 'pt-0' : 'pt-6'">
        <!-- ================================================================== -->
        <!-- Entity Header (variant: 0=SpvOverviewHeader+tabs-slot 3=frosted-sticky-tabs+navy-card) -->
        <!-- ================================================================== -->

        <!-- OPTION 0: Current (unchanged) -->
        <template v-if="headerVariant === 0">
          <SpvOverviewHeader :title="mock.entity.name">
            <template #stage-pill>
              <div class="relative" ref="stageMenuRef">
                <button
                  class="flex cursor-pointer items-center rounded-full border-none bg-white py-[5px] pl-[14px] pr-[10px] text-[13px] leading-none shadow-sm ring-1 ring-gray-700/20 transition-all duration-150 ease-[cubic-bezier(0.22,1,0.36,1)] hover:shadow-md hover:ring-gray-700/30 active:translate-y-px active:shadow-sm"
                  :style="{ fontFeatureSettings: 'tnum', fontVariantNumeric: 'tabular-nums' }"
                  @click="stageMenuOpen = !stageMenuOpen"
                >
                  <span
                    class="mr-1.5 h-1.5 w-1.5 rounded-full"
                    :class="currentState === 'exited' ? 'bg-teal-600' : 'bg-sky-600'"
                  />
                  <span class="font-medium text-gray-700">{{ stageLabel }}</span>
                  <ChevronDown
                    :size="12"
                    class="ml-1 text-gray-400 transition-transform duration-150"
                    :class="{ 'rotate-180': stageMenuOpen }"
                  />
                </button>
                <div
                  v-if="stageMenuOpen"
                  class="absolute right-0 top-[calc(100%+8px)] z-[60] min-w-[180px] origin-top rounded-xl bg-white p-1.5 shadow-[0_4px_24px_rgba(0,0,0,0.08),0_0_0_1px_rgba(0,0,0,0.06)]"
                >
                  <button
                    v-for="(s, idx) in states"
                    :key="s.key"
                    class="flex w-full cursor-pointer items-center gap-2.5 rounded-lg border-none bg-transparent px-3 py-2 text-left text-[13px] font-medium text-gray-700 transition-colors duration-100 hover:bg-gray-100 active:bg-gray-200"
                    :class="
                      s.key === currentState
                        ? currentState === 'exited'
                          ? 'bg-teal-600/10 font-semibold text-teal-600'
                          : 'bg-sky-600/10 font-semibold text-sky-600'
                        : ''
                    "
                    @click="selectStage(s.key)"
                  >
                    <span
                      class="flex h-[22px] w-[22px] flex-shrink-0 items-center justify-center rounded-full text-[11px] font-semibold"
                      :class="
                        lifecycleSteps[idx].completed
                          ? 'bg-teal-600 text-white'
                          : lifecycleSteps[idx].active
                            ? 'bg-sky-600 text-white'
                            : 'bg-gray-100 text-gray-500'
                      "
                    >
                      <Check v-if="lifecycleSteps[idx].completed" :size="11" />
                      <span v-else>{{ idx + 1 }}</span>
                    </span>
                    {{ s.label }}
                  </button>
                </div>
              </div>
            </template>
            <template #actions>
              <VButton size="sm">Edit</VButton>
            </template>
            <template #tabs>
              <nav class="flex items-end gap-1" aria-label="Tabs">
                <a
                  v-for="tab in tabs"
                  :key="tab"
                  class="cursor-pointer whitespace-nowrap rounded-t-lg px-4 py-2 text-sm font-medium"
                  :class="
                    tab === 'Overview' ? 'bg-white text-gray-900' : 'text-slate-300 hover:bg-white/10 hover:text-white'
                  "
                >
                  {{ tab }}
                </a>
              </nav>
            </template>
          </SpvOverviewHeader>
        </template>

        <!-- OPTION 3: Sticky frosted tab strip only -->
        <template v-if="headerVariant === 3">
          <div
            class="sticky top-0 z-40 -mx-4 mb-6 h-16 bg-white/80 px-4 backdrop-blur-md backdrop-saturate-150 [box-shadow:var(--shadow-ring),var(--shadow-elevation)] sm:-mx-8 sm:px-8"
          >
            <nav class="flex h-full items-center space-x-6" aria-label="Tabs">
              <a
                v-for="tab in tabs"
                :key="tab"
                class="flex h-full cursor-pointer items-end whitespace-nowrap pb-4 text-sm font-medium leading-7"
                :class="
                  tab === 'Overview'
                    ? 'border-b-2 border-[#3b88af] font-semibold text-[#3b88af]'
                    : 'text-gray-700 hover:text-gray-900'
                "
              >
                {{ tab }}
              </a>
            </nav>
          </div>
          <div
            class="mb-6 rounded-xl bg-[#1E2D3D] px-[var(--card-padding-x)] py-[var(--card-padding-y)]"
            style="box-shadow: var(--shadow-ring), var(--shadow-elevation)"
          >
            <div class="flex items-center justify-between">
              <div class="flex items-center gap-3">
                <h2 class="text-2xl font-bold leading-7 text-white">{{ mock.entity.name }}</h2>
                <div class="relative" ref="stageMenuRef">
                  <button
                    class="flex cursor-pointer items-center rounded-full border-none bg-white py-[5px] pl-[14px] pr-[10px] text-[13px] leading-none shadow-sm ring-1 ring-gray-700/20 transition-all duration-150 ease-[cubic-bezier(0.22,1,0.36,1)] hover:shadow-md hover:ring-gray-700/30 active:translate-y-px active:shadow-sm"
                    :style="{ fontFeatureSettings: 'tnum', fontVariantNumeric: 'tabular-nums' }"
                    @click="stageMenuOpen = !stageMenuOpen"
                  >
                    <span
                      class="mr-1.5 h-1.5 w-1.5 rounded-full"
                      :class="currentState === 'exited' ? 'bg-teal-600' : 'bg-sky-600'"
                    />
                    <span class="font-medium text-gray-700">{{ stageLabel }}</span>
                    <ChevronDown
                      :size="12"
                      class="ml-1 text-gray-400 transition-transform duration-150"
                      :class="{ 'rotate-180': stageMenuOpen }"
                    />
                  </button>
                  <div
                    v-if="stageMenuOpen"
                    class="absolute right-0 top-[calc(100%+8px)] z-[60] min-w-[180px] origin-top rounded-xl bg-white p-1.5 shadow-[0_4px_24px_rgba(0,0,0,0.08),0_0_0_1px_rgba(0,0,0,0.06)]"
                  >
                    <button
                      v-for="(s, idx) in states"
                      :key="s.key"
                      class="flex w-full cursor-pointer items-center gap-2.5 rounded-lg border-none bg-transparent px-3 py-2 text-left text-[13px] font-medium text-gray-700 transition-colors duration-100 hover:bg-gray-100 active:bg-gray-200"
                      :class="
                        s.key === currentState
                          ? currentState === 'exited'
                            ? 'bg-teal-600/10 font-semibold text-teal-600'
                            : 'bg-sky-600/10 font-semibold text-sky-600'
                          : ''
                      "
                      @click="selectStage(s.key)"
                    >
                      <span
                        class="flex h-[22px] w-[22px] flex-shrink-0 items-center justify-center rounded-full text-[11px] font-semibold"
                        :class="
                          lifecycleSteps[idx].completed
                            ? 'bg-teal-600 text-white'
                            : lifecycleSteps[idx].active
                              ? 'bg-sky-600 text-white'
                              : 'bg-gray-100 text-gray-500'
                        "
                      >
                        <Check v-if="lifecycleSteps[idx].completed" :size="11" />
                        <span v-else>{{ idx + 1 }}</span>
                      </span>
                      {{ s.label }}
                    </button>
                  </div>
                </div>
              </div>
              <VButton size="sm">Edit</VButton>
            </div>
            <div class="mt-3 flex items-center gap-5 text-sm text-white/60">
              <span>{{ mock.entity.managerName }}</span>
              <span class="text-white/20">|</span>
              <span>{{ mock.entity.ein }}</span>
              <span class="text-white/20">|</span>
              <span>Est. {{ mock.entity.formedDate }}</span>
            </div>
          </div>
        </template>
        <!-- ================================================================== -->
        <!-- KPI Row                                                             -->
        <!-- ================================================================== -->
        <div class="mb-8">
          <VStats :stats="kpiStats" />
        </div>

        <!-- ================================================================== -->
        <!-- Two-Column Body                                                     -->
        <!-- ================================================================== -->
        <div class="flex flex-col gap-8 lg:flex-row">
          <!-- LEFT COLUMN: State-specific content -->
          <div class="min-w-0 lg:w-1/2">
            <!-- RAISING -->
            <template v-if="currentState === 'raising'">
              <VCard>
                <VCardTitle title="Raise Summary" />
                <VMetricCard
                  label="Committed"
                  :value="raisingData.totalCommitted"
                  color="#2E8B91"
                  :paid="raiseCommitted"
                  :pending="raiseRemaining"
                  :total="{ value: mock.entity.targetRaise, label: 'Target' }"
                  class="mb-6"
                />
                <VButton
                  size="md"
                  variant="v-teal"
                  class="mt-4 w-full"
                  style="background-color: #2e8b91; border-color: #256f74"
                  >Add Commitment</VButton
                >

                <div class="mt-5 border-t pt-5">
                  <VDescriptionListDetailed :items="raiseActivityRows" @action-click="handleRaiseAction" />
                </div>
              </VCard>
            </template>

            <!-- EXITED -->
            <template v-if="currentState === 'exited'">
              <div
                class="mb-8 rounded-lg p-6 text-center sm:p-8"
                :style="{
                  backgroundImage:
                    'radial-gradient(circle at 1px 1px, rgba(255,255,255,0.06) 1px, transparent 0), linear-gradient(to right, #4FB8A9, #3783B0)',
                  backgroundSize: '16px 16px, 100%',
                }"
              >
                <div class="mx-auto flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-white/30">
                  <Trophy :size="24" class="text-white" />
                </div>
                <div class="mt-4 text-3xl font-bold text-white sm:text-4xl">
                  {{ mock.exited.grossProceeds }}
                </div>
                <div class="mt-4 text-sm font-semibold uppercase tracking-widest text-white/90">
                  {{ mock.exited.exitType }}
                </div>
                <div class="text-sm text-white/70">{{ mock.exited.exitedDate }}</div>
              </div>
            </template>

            <!-- CLOSED | EXITED -->
            <template v-if="currentState === 'closed' || currentState === 'exited'">
              <div class="space-y-5">
                <VCard>
                  <VCardTitle title="Capital Summary" />
                  <div class="mb-6">
                    <VMetricCard
                      label="Called"
                      value="$350,000"
                      :paid="calledPaid"
                      :pending="calledPending"
                      :total="calledTotal"
                      class="mb-6"
                    />
                  </div>
                  <VDescriptionListDetailed :items="capitalSummaryRows" />
                  <div class="border-t border-[var(--divider-color,#EEEFF0)]"></div>
                  <VOperatingActivity :items="operatingActivityItems" />
                </VCard>
                <VCard>
                  <VCardTitle title="Distribution Summary" />
                  <VMetricCard
                    label="Distributed"
                    value="$300,000"
                    color="#408F93"
                    :paid="distPaid"
                    :pending="distPending"
                    class="mb-4"
                  />
                  <VDescriptionListDetailed :items="distributionSummaryRows" />
                  <div class="border-t border-[var(--divider-color,#EEEFF0)]"></div>
                  <VOperatingActivity
                    :items="distOperatingActivityItems"
                    title="Other"
                    subtitle="fees and recycled funds"
                    :icon="Repeat"
                    icon-color="#7d8ad0"
                  />
                </VCard>
              </div>
            </template>
          </div>

          <!-- RIGHT COLUMN: Deal Card -->
          <div class="min-w-0 lg:w-1/2">
            <VCard>
              <VCardTitle title="Investment Summary" />
              <div class="mb-1 text-lg font-bold text-gray-900">
                {{ mock.entity.companyName }}
              </div>
              <a
                :href="mock.entity.companyWebsite"
                target="_blank"
                rel="noopener"
                class="text-sm text-[#3b88af] hover:underline"
              >
                {{ mock.entity.companyWebsite }}
              </a>

              <p class="mt-3 text-sm leading-relaxed text-gray-600">
                {{ mock.entity.companyDescription }}
              </p>

              <LifecycleDateBanner
                v-if="currentState === 'raising'"
                variant="raising"
                :date="raisingData.closingDate"
              />

              <LifecycleDateBanner v-if="currentState === 'closed'" variant="closed" :date="mock.closed.closedDate" />

              <LifecycleDateBanner v-if="currentState === 'exited'" variant="exited" :date="mock.exited.exitedDate" />

              <VDescriptionList class="mt-4" :items="dealItems" />
              <VDescriptionList class="mt-4" :items="feeItems" />

              <div class="mt-4 border-t border-[var(--divider-color,#EEEFF0)] pt-4">
                <div class="text-sm font-semibold text-gray-800">Files</div>
                <div class="mt-2 space-y-1">
                  <div
                    v-for="file in fileItems"
                    :key="file.name"
                    class="flex items-center justify-between rounded-md py-1.5"
                  >
                    <div class="flex items-center gap-2">
                      <VIcon :name="fileIconForType(file.type)" :size="20" class="shrink-0" />
                      <a class="cursor-pointer text-sm text-gray-700" @click="openFile()">
                        {{ file.name }}
                      </a>
                    </div>
                    <div class="flex items-center gap-2">
                      <span class="text-xs text-gray-400">{{ file.size }}</span>
                      <ActionsMenu @click.stop>
                        <ActionsGroup>
                          <ActionItem text="View" @click="openFile()" />
                          <ActionItem text="Download" @click="downloadFile()" />
                        </ActionsGroup>
                      </ActionsMenu>
                    </div>
                  </div>
                </div>
              </div>
            </VCard>
          </div>
        </div>
      </div>
    </main>
  </div>

  <!-- Dev control bar -->
  <div
    class="fixed bottom-3 right-3 z-[100] flex items-center gap-2 rounded-lg bg-white/80 p-2 opacity-20 shadow-sm backdrop-blur transition-opacity hover:opacity-100"
  >
    <span class="text-[10px] font-medium text-gray-500">H:</span>
    <button
      v-for="v in [0, 3]"
      :key="v"
      class="cursor-pointer rounded px-1.5 py-0.5 text-[10px] font-medium transition-colors"
      :class="headerVariant === v ? 'bg-[#2E8B91] text-white' : 'text-gray-500 hover:bg-gray-200'"
      @click="headerVariant = v"
    >
      {{ v }}
    </button>
    <label class="flex cursor-pointer items-center gap-1 text-[10px] font-medium text-gray-600">
      <input
        type="checkbox"
        v-model="showSpvNav"
        class="h-3 w-3 rounded border-gray-300 text-[#3b88af] focus:ring-[#3b88af]"
      />
      Nav
    </label>
    <label class="flex cursor-pointer items-center gap-1 text-[10px] font-medium text-gray-600">
      <input
        type="checkbox"
        v-model="showSublabels"
        class="h-3 w-3 rounded border-gray-300 text-[#3b88af] focus:ring-[#3b88af]"
      />
      Sub
    </label>
  </div>
</template>

<style>
html,
body {
  overflow: hidden;
}

:root {
  --card-padding-x: 1.5rem;
  --card-padding-y: 1.25rem;
  --divider-color: #ebeced;
  --shadow-color: 215deg 20% 15%;
  --shadow-ring: 0 0 0 1px hsl(var(--shadow-color) / 0.05);
  --shadow-elevation:
    0 1px 2px hsl(var(--shadow-color) / 0.06), 0 2px 4px hsl(var(--shadow-color) / 0.04),
    0 4px 8px hsl(var(--shadow-color) / 0.02);
}

@media (prefers-reduced-motion) {
  *,
  *::before,
  *::after {
    transition-duration: 0s !important;
    animation-duration: 0s !important;
    animation-delay: 0s !important;
  }
}
</style>
